import { Logger } from '../../config/logging/logger';

const fallbackLogger = Logger.create('asArrayPayload');

/**
 * Asserts that a list endpoint actually resolved with a list.
 *
 * The API layer returns axios' raw `data` untyped, so a 200 carrying an HTML interstitial or an
 * error envelope instead of an array used to slip through a blind `as T[]` cast and into a
 * persisted slice (SN-936). Throwing here routes the thunk to `.rejected` instead, so the bad
 * value never reaches a reducer. The raw payload is logged first so the upstream cause stays
 * diagnosable.
 *
 * This is the prevention half of the SN-936 fix; the version-1 migration in storeSetup is the
 * recovery half, dropping lists that were already persisted with the wrong shape.
 *
 * Applied to the dashboard partner lists, the fields that actually crashed. It is only worth adding
 * elsewhere for a list that lands in a persisted slice (onboarding, dashboard, content) — those are
 * the ones where a bad value survives a refresh instead of clearing itself.
 *
 * @param label human-readable name of the list, used in the log line and error message
 * @param logger the calling thunk's scoped logger; omit where the thunk has none
 */
export const asArrayPayload = <T>(
  response: unknown,
  label: string,
  logger: ReturnType<typeof Logger.create> = fallbackLogger,
): T[] => {
  if (!Array.isArray(response)) {
    logger.error(Logger.format(`Expected an array of ${label} but received:`, response));
    throw new Error(`${label} response was not an array`);
  }
  return response as T[];
};
